import { useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, Globe, MoreHorizontal, Plus, Search, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { PageHeader } from '../../components/PageHeader';
import { useResellers, useAdminBusinesses, useAdminDomains } from '../../api/admin';
import { formatDate } from '../../lib/format';

const SCOPE_LABEL = { reseller: 'Reseller', business: 'Business' };

/** Cross-reseller domains list. */
export function Domains() {
  const { data: domains = [] } = useAdminDomains();
  const [scopeFilter, setScopeFilter] = useState<string>('all');
  const [verifiedFilter, setVerifiedFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const filtered = useMemo(() => {
    return domains.filter((d) => {
      if (scopeFilter !== 'all' && d.scope !== scopeFilter) return false;
      if (verifiedFilter === 'verified' && !d.isVerified) return false;
      if (verifiedFilter === 'pending' && d.isVerified) return false;
      if (search && !d.hostname.includes(search.toLowerCase()) && !d.ownerName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [domains, scopeFilter, verifiedFilter, search]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title={<span>Domains <span className="text-zinc-400 dark:text-zinc-500 font-medium tabular-nums">({domains.length})</span></span>}
        subtitle="Every reseller and business custom domain across the platform."
        action={
          <Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setAddOpen(true)}>
            Add domain
          </Button>
        }
      />

      <AddDomainModal open={addOpen} onClose={() => setAddOpen(false)} />

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <Filter
            label="Scope"
            value={scopeFilter === 'all' ? 'All' : SCOPE_LABEL[scopeFilter as 'reseller' | 'business']}
            options={[
              { v: 'all', l: 'All' },
              { v: 'reseller', l: 'Reseller' },
              { v: 'business', l: 'Business' },
            ]}
            onChange={setScopeFilter}
          />
          <Filter
            label="Verification"
            value={verifiedFilter === 'all' ? 'All' : verifiedFilter.charAt(0).toUpperCase() + verifiedFilter.slice(1)}
            options={[
              { v: 'all', l: 'All' },
              { v: 'verified', l: 'Verified' },
              { v: 'pending', l: 'Pending' },
            ]}
            onChange={setVerifiedFilter}
          />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search hostname or owner…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Hostname</th>
                <th className="text-left font-medium px-4 py-3">Scope</th>
                <th className="text-left font-medium px-4 py-3">Owner</th>
                <th className="text-left font-medium px-4 py-3">Verification</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">TLS</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Added</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {filtered.map((d) => (
                <tr key={d.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3">
                    <div className="flex items-center gap-2.5">
                      <Globe className="w-4 h-4 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-950 dark:text-zinc-50 mono text-xs flex items-center gap-2">
                          {d.hostname}
                          {d.isPrimary && <span className="text-[10px] uppercase tracking-wide bg-zinc-950 dark:bg-zinc-50 text-white dark:text-zinc-950 px-1.5 py-px rounded">Primary</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{SCOPE_LABEL[d.scope]}</td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{d.ownerName}</td>
                  <td className="px-4 py-3">
                    <Badge tone={d.isVerified ? 'success' : 'warning'}>
                      {d.isVerified ? 'Verified' : 'Pending'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <Badge tone={d.tlsStatus === 'issued' ? 'success' : d.tlsStatus === 'pending' ? 'warning' : 'error'}>
                      {d.tlsStatus.charAt(0).toUpperCase() + d.tlsStatus.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums hidden lg:table-cell">{formatDate(d.addedAt, { compact: true })}</td>
                  <td className="px-5 sm:px-6 py-3 text-right">
                    <button className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400">
          Showing <span className="tabular-nums">{filtered.length}</span> of <span className="tabular-nums">{domains.length}</span>
        </div>
      </Card>
    </div>
  );
}

function AddDomainModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: resellers = [] } = useResellers();
  const { data: adminBusinesses = [] } = useAdminBusinesses();
  const [hostname, setHostname] = useState('');
  const [scope, setScope] = useState<'reseller' | 'business'>('reseller');
  const [ownerId, setOwnerId] = useState('');
  const [step, setStep] = useState<'form' | 'verify' | 'done'>('form');

  function reset() {
    setHostname('');
    setScope('reseller');
    setOwnerId('');
    setStep('form');
  }

  function close() {
    onClose();
    setTimeout(reset, 250);
  }

  function next() {
    if (!hostname.trim() || !ownerId) return;
    setStep('verify');
  }

  function done() {
    setStep('done');
    setTimeout(close, 1200);
  }

  const owners =
    scope === 'reseller'
      ? resellers.map((r) => ({ id: r.id, name: r.name }))
      : adminBusinesses.map((b) => ({ id: b.id, name: b.name }));

  return (
    <Modal
      open={open}
      onClose={close}
      title={step === 'verify' ? 'Verify DNS' : step === 'done' ? 'Domain added' : 'Add domain'}
      size="lg"
      footer={
        step === 'form' ? (
          <>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button onClick={next} disabled={!hostname.trim() || !ownerId}>Next: verify DNS</Button>
          </>
        ) : step === 'verify' ? (
          <>
            <Button variant="ghost" onClick={() => setStep('form')}>Back</Button>
            <Button onClick={done}>I've added the CNAME</Button>
          </>
        ) : null
      }
    >
      {step === 'form' && (
        <div className="space-y-5">
          <Input
            label="Hostname"
            required
            placeholder="acme-resellers.com or pos.your-shop.com"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            hint="Apex domain or subdomain. Customers will reach the service here after CNAME verification."
          />

          <fieldset>
            <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Scope</legend>
            <div className="space-y-2">
              <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'reseller'}
                  onChange={() => { setScope('reseller'); setOwnerId(''); }}
                  className="text-brand focus:ring-brand"
                />
                <span><strong>Reseller</strong> — domain hosts a reseller's branded portal + signup</span>
              </label>
              <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'business'}
                  onChange={() => { setScope('business'); setOwnerId(''); }}
                  className="text-brand focus:ring-brand"
                />
                <span><strong>Business</strong> — domain hosts a single business's POS</span>
              </label>
            </div>
          </fieldset>

          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
              {scope === 'reseller' ? 'Reseller' : 'Business'} <span className="text-rose-600">*</span>
            </label>
            <select
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className="block w-full px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand transition"
            >
              <option value="">Select…</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {step === 'verify' && (
        <div className="space-y-5">
          <p className="text-sm text-zinc-700 dark:text-zinc-200">
            Add this CNAME to <strong className="mono">{hostname}</strong>'s DNS. We auto-check every 30 seconds.
          </p>
          <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 p-4 mono text-xs">
            <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1.5">
              <span className="text-zinc-500 dark:text-zinc-400">Type</span>
              <span className="text-zinc-900 dark:text-zinc-100">CNAME</span>
              <span className="text-zinc-500 dark:text-zinc-400">Name</span>
              <span className="text-zinc-900 dark:text-zinc-100 break-all">{hostname}</span>
              <span className="text-zinc-500 dark:text-zinc-400">Target</span>
              <span className="text-zinc-900 dark:text-zinc-100">cname.itcitysoft.com</span>
              <span className="text-zinc-500 dark:text-zinc-400">TTL</span>
              <span className="text-zinc-900 dark:text-zinc-100">300 (auto)</span>
            </div>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" />
            TLS certificate auto-provisioned via Let's Encrypt once DNS verifies.
          </p>
        </div>
      )}

      {step === 'done' && (
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center text-emerald-700 dark:text-emerald-300 mb-4">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">{hostname} added</h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Verifying DNS in the background. TLS will issue once the CNAME resolves.
          </p>
        </div>
      )}
    </Modal>
  );
}

function Filter({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[160px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className="block w-full text-left px-3 py-1.5 text-xs text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
